import { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Table, Alert } from "react-bootstrap";
import { 
  getAree, addArea, updateArea, deleteArea, 
  getDiscipline, addDisciplina, updateDisciplina, deleteDisciplina,
  getParameters, addParameter, updateParameter, deleteParameter
} from "../api/api.js";

export default function GestioneParametri() {
  const [aree, setAree] = useState([]);
  const [discipline, setDiscipline] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [newArea, setNewArea] = useState("");
  const [newDisciplina, setNewDisciplina] = useState("");
  const [newDisciplinaCosto, setNewDisciplinaCosto] = useState(0);
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");
  const [message, setMessage] = useState("");

  // paginazione locale
  const [areaPage, setAreaPage] = useState(1);
  const [discPage, setDiscPage] = useState(1);
  const [paramPage, setParamPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const paginate = (arr, page) => {
    const start = (page - 1) * pageSize;
    return arr.slice(start, start + pageSize);
  };

  const loadAll = async () => {
    try {
      const [aRes, dRes, pRes] = await Promise.all([
        getAree(), getDiscipline(), getParameters()
      ]);
      setAree(aRes?.data || []);
      setDiscipline(dRes?.data || []);
      setParameters(pRes?.data || []);
      setPageSize(Number(pRes.data.find(p => p.key === 'RECORDS_PER_PAGE')?.value) || 5);

      // non resettare le pagine
    } catch (err) {
      console.error(err);
      setMessage("Errore durante il caricamento dei dati");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // ===== AREE =====
  const handleAddArea = async () => {
    if (!newArea.trim()) return;
    try {
      await addArea({ nome: newArea });
      setNewArea("");
      const newPage = Math.max(1, Math.ceil((aree.length + 1) / pageSize));
      setAreaPage(newPage);
      loadAll();
    } catch (err) {
      console.error(err);
      setMessage("Errore durante l'aggiunta dell'area");
    }
  };

  const handleDeleteArea = async (id) => {
    try {
      await deleteArea(id);
      const maxPage = Math.max(1, Math.ceil((aree.length - 1) / pageSize));
      setAreaPage(prev => Math.min(prev, maxPage));
      loadAll();
    } catch (err) {
      console.error(err);
      setMessage("Errore durante la cancellazione dell'area");
    }
  };

  const handleUpdateArea = async (id, nome) => {
    if (!nome.trim()) return;
    try {
      await updateArea(id, { nome });
      // aggiorna solo la lista locale
      setAree(prev => prev.map(a => a.id === id ? { ...a, nome } : a));
    } catch (err) {
      console.error(err);
      setMessage("Errore durante l'aggiornamento dell'area");
    }
  };

  // ===== DISCIPLINE =====
  const handleAddDisciplina = async () => {
    if (!newDisciplina.trim()) return;
    try {
      await addDisciplina({ nome: newDisciplina, costo: newDisciplinaCosto || 0 });
      setNewDisciplina(""); 
      setNewDisciplinaCosto(0);
      const newPage = Math.max(1, Math.ceil((discipline.length + 1) / pageSize));
      setDiscPage(newPage);
      loadAll();
    } catch (err) {
      console.error(err);
      setMessage("Errore durante l'aggiunta della disciplina");
    }
  };

  const handleDeleteDisciplina = async (id) => {
    try {
      await deleteDisciplina(id);
      const maxPage = Math.max(1, Math.ceil((discipline.length - 1) / pageSize));
      setDiscPage(prev => Math.min(prev, maxPage));
      loadAll();
    } catch (err) {
      console.error(err);
      setMessage("Errore durante la cancellazione della disciplina");
    }
  };

  const handleUpdateDisciplina = async (id, nome, costo) => {
    if (!nome.trim()) return;
    try {
      await updateDisciplina(id, { nome, costo });
      setDiscipline(prev => prev.map(d => d.id === id ? { ...d, nome, costo } : d));
    } catch (err) {
      console.error(err);
      setMessage("Errore durante l'aggiornamento della disciplina");
    }
  };

  // ===== PARAMETERS =====
  const handleAddParameter = async () => {
    if (!newParamKey.trim()) return;
    try {
      await addParameter({ key: newParamKey, value: newParamValue });
      setNewParamKey(""); 
      setNewParamValue("");
      const newPage = Math.max(1, Math.ceil((parameters.length + 1) / pageSize));
      setParamPage(newPage);
      loadAll();
    } catch (err) {
      console.error(err);
      setMessage("Errore durante l'aggiunta del parametro");
    }
  };

  const handleDeleteParameter = async (id) => {
    try {
      await deleteParameter(id);
      const maxPage = Math.max(1, Math.ceil((parameters.length - 1) / pageSize));
      setParamPage(prev => Math.min(prev, maxPage));
      loadAll();
    } catch (err) {
      console.error(err);
      setMessage("Errore durante la cancellazione del parametro");
    }
  };

  const handleUpdateParameter = async (id, key, value) => {
    if (!key.trim()) return;
    try {
      await updateParameter(id, { key, value });
      setParameters(prev => prev.map(p => p.id === id ? { ...p, key, value } : p));
    } catch (err) {
      console.error(err);
      setMessage("Errore durante l'aggiornamento del parametro");
    }
  };

  return (
    <Container className="py-4">
      <h3 className="mb-4">Gestione Parametri</h3>
      {message && <Alert variant="info">{message}</Alert>}

      <Row>
        {/* ===== AREE ===== */}
        <Col md={4}>
          <h5>Aree di Gara</h5>
          <Form className="d-flex mb-2">
            <Form.Control type="text" placeholder="Nome area"
              value={newArea} onChange={(e) => setNewArea(e.target.value)} />
            <Button onClick={handleAddArea} className="ms-2">Aggiungi</Button>
          </Form>
          <Table size="sm" bordered>
            <tbody>
              {paginate(aree, areaPage).map(a => (
                <tr key={a.id}>
                  <td>
                    <Form.Control type="text" value={a.nome}
                      onChange={e => setAree(prev => prev.map(i => i.id === a.id ? {...i, nome: e.target.value} : i))}
                      onBlur={() => handleUpdateArea(a.id, a.nome)} />
                  </td>
                  <td width="1%">
                    <Button size="sm" variant="danger" onClick={() => handleDeleteArea(a.id)}>✕</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between mt-2">
            <Button size="sm" disabled={areaPage === 1} onClick={() => setAreaPage(areaPage - 1)}>Prev</Button>
            <div>Pagina {areaPage} / {Math.max(1, Math.ceil(aree.length / pageSize))}</div>
            <Button size="sm" disabled={areaPage >= Math.ceil(aree.length / pageSize)} onClick={() => setAreaPage(areaPage + 1)}>Next</Button>
          </div>
        </Col>

        {/* ===== DISCIPLINE ===== */}
        <Col md={4}>
          <h5>Discipline</h5>
          <Form className="d-flex mb-2">
            <Form.Control type="text" placeholder="Nome disciplina"
              value={newDisciplina} onChange={(e) => setNewDisciplina(e.target.value)} />
            <Form.Control type="number" placeholder="Costo"
              value={newDisciplinaCosto} onChange={(e) => setNewDisciplinaCosto(Number(e.target.value))}
              className="ms-2" />
            <Button onClick={handleAddDisciplina} className="ms-2">Aggiungi</Button>
          </Form>
          <Table size="sm" bordered>
            <thead><tr><th>Nome</th><th>Costo</th><th></th></tr></thead>
            <tbody>
              {paginate(discipline, discPage).map(d => (
                <tr key={d.id}>
                  <td>
                    <Form.Control type="text" value={d.nome}
                      onChange={e => setDiscipline(prev => prev.map(i => i.id === d.id ? {...i, nome: e.target.value} : i))}
                      onBlur={() => handleUpdateDisciplina(d.id, d.nome, d.costo)} />
                  </td>
                  <td width="100">
                    <Form.Control type="number" value={d.costo}
                      onChange={e => setDiscipline(prev => prev.map(i => i.id === d.id ? {...i, costo: Number(e.target.value)} : i))}
                      onBlur={() => handleUpdateDisciplina(d.id, d.nome, d.costo)} />
                  </td>
                  <td width="1%">
                    <Button size="sm" variant="danger" onClick={() => handleDeleteDisciplina(d.id)}>✕</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between mt-2">
            <Button size="sm" disabled={discPage === 1} onClick={() => setDiscPage(discPage - 1)}>Prev</Button>
            <div>Pagina {discPage} / {Math.max(1, Math.ceil(discipline.length / pageSize))}</div>
            <Button size="sm" disabled={discPage >= Math.ceil(discipline.length / pageSize)} onClick={() => setDiscPage(discPage + 1)}>Next</Button>
          </div>
        </Col>

        {/* ===== PARAMETERS ===== */}
        <Col md={4}>
          <h5>Parametri di Sistema</h5>
          <Form className="d-flex mb-2">
            <Form.Control type="text" placeholder="Chiave"
              value={newParamKey} onChange={(e) => setNewParamKey(e.target.value)} />
            <Form.Control type="text" placeholder="Valore"
              value={newParamValue} onChange={(e) => setNewParamValue(e.target.value)}
              className="ms-2" />
            <Button onClick={handleAddParameter} className="ms-2">Aggiungi</Button>
          </Form>
          <Table size="sm" bordered>
            <thead><tr><th>Chiave</th><th>Valore</th><th></th></tr></thead>
            <tbody>
              {paginate(parameters, paramPage).map(p => (
                <tr key={p.id}>
                  <td>
                    <Form.Control type="text" value={p.key}
                      onChange={e => setParameters(prev => prev.map(i => i.id === p.id ? {...i, key: e.target.value} : i))}
                      onBlur={() => handleUpdateParameter(p.id, p.key, p.value)} />
                  </td>
                  <td>
                    <Form.Control type="text" value={p.value}
                      onChange={e => setParameters(prev => prev.map(i => i.id === p.id ? {...i, value: e.target.value} : i))}
                      onBlur={() => handleUpdateParameter(p.id, p.key, p.value)} />
                  </td>
                  <td width="1%">
                    <Button size="sm" variant="danger" onClick={() => handleDeleteParameter(p.id)}>✕</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between mt-2">
            <Button size="sm" disabled={paramPage === 1} onClick={() => setParamPage(paramPage - 1)}>Prev</Button>
            <div>Pagina {paramPage} / {Math.max(1, Math.ceil(parameters.length / pageSize))}</div>
            <Button size="sm" disabled={paramPage >= Math.ceil(parameters.length / pageSize)} onClick={() => setParamPage(paramPage + 1)}>Next</Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
